import { Link, useLocation } from 'react-router-dom'
import type { NodeMetrics, NodeSummary } from '@/types'
import NodeStatusBadge from './NodeStatusBadge'
import MetricBar, { formatBytes, formatPercent } from './MetricBar'

interface Props {
  nodes: NodeSummary[]
  showSiteColumn?: boolean
}

function formatHeartbeat(ts: string | null): string {
  if (!ts) return '—'
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000)
  if (diff < 60) return `${diff}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  return `${Math.floor(diff / 3600)}h ago`
}

function diskUsedBytes(metrics: NodeMetrics): number | null {
  if (metrics.disk_total_bytes != null && metrics.disk_free_bytes != null) {
    return Math.max(metrics.disk_total_bytes - metrics.disk_free_bytes, 0)
  }
  if (metrics.disk_total_bytes != null && metrics.disk_percent != null) {
    return metrics.disk_total_bytes * (metrics.disk_percent / 100)
  }
  return null
}

function DiskUsageCell({ metrics }: { metrics: NodeMetrics | null }) {
  if (!metrics || (metrics.disk_percent == null && metrics.disk_total_bytes == null && metrics.disk_free_bytes == null)) {
    return <span style={{ color: '#aaa' }}>-</span>
  }

  const usedBytes = diskUsedBytes(metrics)
  const usageText = metrics.disk_total_bytes == null
    ? metrics.disk_free_bytes == null ? 'Usage -' : `${formatBytes(metrics.disk_free_bytes)} free`
    : usedBytes == null
      ? `Total ${formatBytes(metrics.disk_total_bytes)}`
      : `${formatBytes(usedBytes)} / ${formatBytes(metrics.disk_total_bytes)}`

  return (
    <div style={diskCellStyle}>
      <div style={diskBarRowStyle}>
        <div style={{ flex: 1 }}>
          <MetricBar value={metrics.disk_percent} />
        </div>
        <span style={diskPercentStyle}>{formatPercent(metrics.disk_percent)}</span>
      </div>
      <div style={diskTextStyle}>{usageText}</div>
    </div>
  )
}

type VideoBadgeTone = 'active' | 'warning' | 'muted'

interface VideoBadge {
  key: string
  label: string
  tone: VideoBadgeTone
}

function isActiveStatus(status: string | null | undefined): boolean {
  return Boolean(status && !['idle', 'off', 'unsupported', 'unknown'].includes(status))
}

function shortStatus(status: string): string {
  const labels: Record<string, string> = {
    local_running: 'Local',
    running: 'Running',
    starting: 'Starting',
    stopping: 'Stopping',
    uploading: 'Uploading',
    on: 'On',
  }
  return labels[status] ?? status
}

function buildVideoBadges(node: NodeSummary): VideoBadge[] {
  const status = node.video_status
  if (!status) {
    const legacy = node.inference_auto_upload
    return legacy?.status === 'on'
      ? [{ key: 'inference-auto', label: 'Infer Auto: On', tone: 'active' }]
      : []
  }

  const badges: VideoBadge[] = []
  if (isActiveStatus(status.manual.recording)) {
    badges.push({
      key: 'manual-recording',
      label: `Manual Rec: ${shortStatus(status.manual.recording)}`,
      tone: status.manual.recording === 'local_running' ? 'warning' : 'active',
    })
  }
  if (isActiveStatus(status.manual.upload)) {
    badges.push({
      key: 'manual-upload',
      label: `Manual Upload: ${shortStatus(status.manual.upload)}`,
      tone: 'active',
    })
  }
  if (isActiveStatus(status.manual.auto_upload)) {
    badges.push({
      key: 'manual-auto',
      label: `Manual Auto: ${shortStatus(status.manual.auto_upload)}`,
      tone: 'active',
    })
  }
  if (isActiveStatus(status.inference.recording)) {
    badges.push({
      key: 'inference-recording',
      label: `Infer Rec: ${shortStatus(status.inference.recording)}`,
      tone: 'active',
    })
  }
  if (isActiveStatus(status.inference.upload)) {
    badges.push({
      key: 'inference-upload',
      label: `Infer Upload: ${shortStatus(status.inference.upload)}`,
      tone: 'active',
    })
  }
  if (isActiveStatus(status.inference.auto_upload)) {
    badges.push({
      key: 'inference-auto',
      label: `Infer Auto: ${shortStatus(status.inference.auto_upload)}`,
      tone: 'active',
    })
  }
  return badges
}

function VideoStatusCell({ node }: { node: NodeSummary }) {
  const badges = buildVideoBadges(node)
  if (badges.length === 0) {
    return <span style={videoStatusMutedStyle}>Idle</span>
  }

  return (
    <div style={videoStatusCellStyle}>
      {badges.map((badge) => (
        <span key={badge.key} style={{ ...videoBadgeStyle, ...videoBadgeToneStyle[badge.tone] }}>
          {badge.label}
        </span>
      ))}
    </div>
  )
}

export default function NodeTable({ nodes, showSiteColumn = true }: Props) {
  const location = useLocation()
  const returnTo = `${location.pathname}${location.search}`

  if (nodes.length === 0) {
    return <p style={{ color: '#888', marginTop: 32, textAlign: 'center' }}>No nodes match the current filters.</p>
  }

  const headers = showSiteColumn
    ? ['Status', 'Node', 'Site', 'Disk', 'Video status', 'Last heartbeat', 'Agent ver.', 'Hostname', 'Actions']
    : ['Status', 'Node', 'Disk', 'Video status', 'Last heartbeat', 'Agent ver.', 'Hostname', 'Actions']

  return (
    <div style={tableScrollStyle}>
      <table style={tableStyle}>
        <colgroup>
          <col style={{ width: 108 }} />
          <col style={{ width: 220 }} />
          {showSiteColumn && <col style={{ width: 150 }} />}
          <col style={{ width: 210 }} />
          <col style={{ width: 210 }} />
          <col style={{ width: 150 }} />
          <col style={{ width: 110 }} />
          <col style={{ width: 220 }} />
          <col style={{ width: 100 }} />
        </colgroup>
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h} style={thStyle}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {nodes.map((node, i) => (
            <tr key={node.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
              <td style={tdStyle}>
                <NodeStatusBadge status={node.status} />
              </td>
              <td style={{ ...tdStyle, ...nodeNameCellStyle }}>
                <Link
                  to={`/nodes/${node.id}`}
                  state={{ returnTo }}
                  title={node.name ?? node.node_key}
                  style={nodeNameLinkStyle}
                >
                  {node.name ?? node.node_key}
                </Link>
              </td>
              {showSiteColumn && <td style={tdStyle}>{node.site ?? '—'}</td>}
              <td style={tdStyle}>
                <DiskUsageCell metrics={node.latest_metrics} />
              </td>
              <td style={tdStyle}>
                <VideoStatusCell node={node} />
              </td>
              <td style={{ ...tdStyle, color: node.status === 'offline' ? '#c62828' : undefined }}>
                {formatHeartbeat(node.last_seen_at)}
              </td>
              <td style={{ ...tdStyle, color: '#888', fontSize: 12 }}>{node.agent_version ?? '—'}</td>
              <td style={{ ...tdStyle, color: '#888', fontSize: 12 }}>{node.hostname ?? '—'}</td>
              <td style={tdStyle}>
                <Link
                  to={`/nodes/${node.id}`}
                  state={{ returnTo }}
                  style={{ fontSize: 12, color: '#3949ab', textDecoration: 'none' }}
                >
                  Detail
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const tableScrollStyle: React.CSSProperties = {
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  minWidth: 1130,
  tableLayout: 'fixed',
  borderCollapse: 'collapse',
  fontSize: 14,
  background: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: 6,
  overflow: 'hidden',
}

const thStyle: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 700,
  color: '#555',
  background: '#f0f0f4',
  borderBottom: '1px solid #e0e0e0',
  whiteSpace: 'nowrap',
}

const tdStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderBottom: '1px solid #f0f0f0',
  verticalAlign: 'middle',
  overflowWrap: 'anywhere',
}

const nodeNameCellStyle: React.CSSProperties = {
  width: 220,
  minWidth: 220,
  maxWidth: 220,
  overflow: 'hidden',
  overflowWrap: 'normal',
  whiteSpace: 'nowrap',
  fontWeight: 600,
}

const nodeNameLinkStyle: React.CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: '#1a1a2e',
  textDecoration: 'none',
}

const diskCellStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 0,
}

const diskBarRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
}

const diskPercentStyle: React.CSSProperties = {
  minWidth: 34,
  textAlign: 'right',
  fontSize: 12,
  color: '#555',
}

const diskTextStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#666',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

const videoStatusCellStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  minWidth: 0,
}

const videoBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  maxWidth: '100%',
  padding: '2px 7px',
  borderRadius: 8,
  fontSize: 11,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

const videoBadgeToneStyle: Record<VideoBadgeTone, React.CSSProperties> = {
  active: { background: '#e8f5e9', color: '#2e7d32' },
  warning: { background: '#fff8e1', color: '#8a5a00' },
  muted: { background: '#f2f3f5', color: '#666' },
}

const videoStatusMutedStyle: React.CSSProperties = {
  color: '#aaa',
  fontSize: 12,
}
